import { isPlatformServer } from '@angular/common';
import { DOCUMENT, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';

import { BehaviorSubject, Observable, first, firstValueFrom, map } from 'rxjs';

import { MediaQueryService } from '../browser/media-query.service';
import { WindowEvent } from '../browser/window/window-ref.service';
import { Logger } from '../logging/logger';
import { LazyAsset } from './lazy-assets.client-config';

/**
 * @whatItDoes Dynamically/lazily loads styles.
 *
 * @howToUse
 * ```
 * this.stylesService.load('http://cnd.com/style.css');
 * this.stylesService.load('custom'); // Alias set in StylesheetBootstrapAsset.Alias with StylesheetLazyLoadStrategy.Custom on the server.
 * ```
 *
 * @stable
 */
@Injectable({ providedIn: 'root' })
export class StylesService {
    private readonly initialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly stylesInDom = new Map<string, Promise<HTMLLinkElement | null>>();
    private readonly aliasMap = new Map<string, string>();
    private readonly styleMap = new Map<string, LazyAsset>();
    private readonly styleCssMap = new Map<string, string>();

    get stylesInitialized(): Observable<void> {
        return this.initialized.pipe(
            first((ready) => ready),
            map(() => {}),
        );
    }

    private readonly _doc = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly mediaQueryService = inject(MediaQueryService);
    private readonly zone = inject(NgZone);
    private readonly log = inject(Logger);

    init(stylesheets: LazyAsset[]) {
        stylesheets.forEach((s) => {
            if (s.alias) {
                this.aliasMap.set(s.alias, s.url);
            }

            this.styleMap.set(s.url, s);
        });
        this.initialized.next(true);
    }

    load(aliasOrPath: string): Promise<any> {
        return this.zone.runOutsideAngular(async () => {
            const href = await this.resolve(aliasOrPath);
            return this.loadStyle(href);
        });
    }

    async addStyle(id: string, content: string): Promise<void | HTMLStyleElement> {
        if (this.styleCssMap.has(id)) return;

        this.styleCssMap.set(id, content);

        return this.injectCssStyle(id, content).catch((e: unknown) => {
            this.log.error(`Failed loading raw style ${id} with ${content}.`, e);
        });
    }

    removeStyle(id: string) {
        if (this.checkEncodeURI(id)) {
            id = decodeURI(id);
        }
        const element = this._doc.querySelectorAll(`style[id="${id}"]`)[0];
        element?.remove();
        const removed = this.styleCssMap.delete(id);
        this.log.info(`Removed dom style ${id}, item ${removed} element ${!!element}`);
    }

    checkEncodeURI(str: string): boolean {
        return /%/i.test(str);
    }

    async loadStyle(style: LazyAsset): Promise<HTMLLinkElement | null> {
        if (style.lazyLoad === 1 && !this.stylesInDom.has(style.url)) {
            const link = this._doc.head.querySelector<HTMLLinkElement>(`link[rel="stylesheet"][href="${style.url}"]`);

            if (link) {
                this.stylesInDom.set(style.url, Promise.resolve(link));
            }
        }

        if (!this.stylesInDom.has(style.url)) {
            let media: string | undefined;

            if (style.media) {
                media = this.mediaQueryService.toMediaQuery(style.media) as string;
            }

            const promise = this.injectCss(style.url, media).catch((e: unknown) => {
                this.log.error(`Failed lazy loading style ${style.url}.`, e);

                return null;
            });

            this.stylesInDom.set(style.url, promise);
        }

        return Promise.resolve(this.stylesInDom.get(style.url)!);
    }

    private async resolve(aliasOrPath: string): Promise<LazyAsset> {
        let url = aliasOrPath;

        await firstValueFrom(this.stylesInitialized);

        if (this.aliasMap.has(aliasOrPath)) {
            url = this.aliasMap.get(aliasOrPath)!;
        }

        if (this.styleMap.has(url)) {
            return Promise.resolve(this.styleMap.get(url)!);
        }

        throw new Error(`Alias or path ${aliasOrPath} is not known.`);
    }

    private linkTagListenerPromise<T extends HTMLLinkElement | HTMLStyleElement>(element: T) {
        if (isPlatformServer(this.platformId)) {
            return Promise.resolve(element);
        }

        return new Promise<T>((resolve, reject) => {
            element.addEventListener(WindowEvent.Load, () => resolve(element));
            element.addEventListener(WindowEvent.Error, () => reject(element));
        });
    }

    private injectCss(href: string, media?: string): Promise<HTMLLinkElement> {
        const element = this._doc.createElement('link');
        element.rel = 'stylesheet';
        element.type = 'text/css';
        element.href = href;
        if (media) {
            element.media = media;
        }

        const promise = this.linkTagListenerPromise(element);

        this._doc.head.appendChild(element);

        return promise;
    }

    private injectCssStyle(id: string, content: string): Promise<HTMLStyleElement> {
        const element = this._doc.createElement('style');
        element.id = id;
        element.innerHTML = content;

        const promise = this.linkTagListenerPromise(element);

        this._doc.body.appendChild(element);

        return promise;
    }
}
